import {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  Timestamp,
  deleteField,
} from "firebase/firestore";
import { db } from "./firestore";
import { postApi } from "./apiService";
import {
  getAdminDetailsFromStorage,
  getAdminUserIdFromDetails,
} from "../Utils/headOfLineRole";

/** Coerce backend user ids to strings for Firestore keys and chat document ids. */
const toUserIdString = (id) => (id == null ? "" : String(id));

/**
 * User-facing message for Firestore / messaging failures (e.g. after Firebase project migration).
 */
export const getMessagingErrorMessage = (error, fallback = "Failed to create chat. Please try again.") => {
  if (!error) return fallback;
  const code = error?.code || "";

  if (code === "permission-denied") {
    return (
      "Chat access denied. In Firebase Console (famconnect-a10e6), enable Firestore and " +
      "deploy the rules from firestore.rules in this repo (Firestore → Rules)."
    );
  }
  if (code === "unavailable" || code === "resource-exhausted") {
    return "Chat service is temporarily unavailable. Please try again.";
  }
  if (code === "failed-precondition" || code === "not-found") {
    return "Firestore is not set up for this app. Create a Firestore database in the famconnect-a10e6 project.";
  }
  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message;
  }
  return fallback;
};

/**
 * MESSAGING SERVICE
 * 
 * Database Structure:
 * 
 * Collection: 'chats'
 *   Document ID: auto-generated
 *   Fields:
 *     - type: 'individual' | 'group'
 *     - participants: [userId1, userId2, ...] (sorted array for individual chats)
 *     - groupName: string (only for group chats)
 *     - createdBy: userId
 *     - createdAt: timestamp
 *     - updatedAt: timestamp
 *     - lastMessage: { text, senderId, timestamp } (optional)
 *     - lastMessageAt: timestamp
 * 
 * Subcollection: 'messages' (under each chat document)
 *   Document ID: auto-generated
 *   Fields:
 *     - text: string
 *     - senderId: userId
 *     - senderName: string (optional, for display)
 *     - timestamp: timestamp
 *     - readBy: [userId1, userId2, ...]
 *     - type: 'text' | 'image' | 'file' (default: 'text')
 *     - fileUrl: string (optional, S3 URL for attachments)
 *     - fileName: string (optional, original file name)
 *     - mimeType: string (optional)
 *     - fileExtension: string (optional, e.g. pdf, docx)
 */

const extractUploadedFileUrl = (response) => {
  const candidates = [
    response?.data?.imageUrl,
    response?.data?.url,
    response?.data?.image,
    response?.data?.s3_link,
    response?.data?.s3Link,
    response?.data?.file_url,
    response?.data?.fileUrl,
    typeof response?.data === "string" ? response.data : null,
    response?.imageUrl,
    response?.url,
    response?.image,
    response?.s3_link,
    response?.s3Link,
  ];
  return candidates.find((value) => typeof value === "string" && value.trim())?.trim() || null;
};

/**
 * Upload a chat attachment to S3 via the backend.
 * @param {File} file - File to upload (sent as multipart field `image`)
 * @returns {Promise<string>} - Public S3 URL
 */
export const uploadChatImage = async (file) => {
  const formData = new FormData();
  formData.append("image", file);

  const response = await postApi("/chat-image", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  if (response?.statusCode && response.statusCode !== 200) {
    throw new Error(response?.message || "Failed to upload file.");
  }

  const fileUrl = extractUploadedFileUrl(response);
  if (!fileUrl) {
    throw new Error("Upload succeeded but no file URL was returned.");
  }

  return fileUrl;
};

/**
 * Create an individual chat room between two users
 * @param {string} userId1 - First user ID
 * @param {string} userId2 - Second user ID
 * @param {string} familyTreeId - Family tree ID
 * @param {Object} participant1Info - Optional: First user's member info (name, color, head of line)
 * @param {Object} participant2Info - Optional: Second user's member info (name, color, head of line)
 * @returns {Promise<string>} - Chat room ID
 */
export const createIndividualChat = async (userId1, userId2, familyTreeId, participant1Info = null, participant2Info = null) => {
  try {
    const uid1 = toUserIdString(userId1);
    const uid2 = toUserIdString(userId2);
    const treeId = toUserIdString(familyTreeId);

    if (!uid1 || !uid2) {
      throw new Error("Missing user information for chat.");
    }
    if (!treeId) {
      throw new Error("Missing family tree information for chat.");
    }

    // Sort participant IDs to ensure consistency (same chat for same two users)
    const participants = [uid1, uid2].sort();
    
    // Generate deterministic chat ID: family_tree_id_userId1_userId2
    const chatId = `${treeId}_${participants[0]}_${participants[1]}`;

    // Check if chat already exists
    const chatRef = doc(db, "chats", chatId);
    const chatSnap = await getDoc(chatRef);

    if (chatSnap.exists()) {
      // Update existing chat with participant info if provided
      const updateData = {};
      
      if (participant1Info) {
        // Use user_id from participant1Info, fallback to userId1
        const participant1UserId = toUserIdString(participant1Info.user_id || uid1);
        updateData[`participantInfo.${participant1UserId}`] = {
          name: participant1Info.name || participant1Info.first_name || "",
          first_name: participant1Info.first_name || "",
          suffix: participant1Info.suffix || null,
          special_name: participant1Info.special_name || null,
          color_name: participant1Info.color_name || "",
          hex_code: participant1Info.hex_code || "#000000",
          head_first_name: participant1Info.head_first_name || "",
          head_suffix: participant1Info.head_suffix || null,
          head_special_name: participant1Info.head_special_name || null,
        };
      }
      
      if (participant2Info) {
        // Use user_id from participant2Info, fallback to userId2
        const participant2UserId = toUserIdString(participant2Info.user_id || uid2);
        updateData[`participantInfo.${participant2UserId}`] = {
          name: participant2Info.name || participant2Info.first_name || "",
          first_name: participant2Info.first_name || "",
          suffix: participant2Info.suffix || null,
          special_name: participant2Info.special_name || null,
          color_name: participant2Info.color_name || "",
          hex_code: participant2Info.hex_code || "#000000",
          head_first_name: participant2Info.head_first_name || "",
          head_suffix: participant2Info.head_suffix || null,
          head_special_name: participant2Info.head_special_name || null,
        };
      }
      
      if (Object.keys(updateData).length > 0) {
        await updateDoc(chatRef, updateData);
      }
      
      return chatId;
    }

    // Create new chat with participant info
    const chatData = {
      type: "individual",
      family_tree_id: treeId,
      participants: participants,
      createdBy: uid1,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    
    // Add participant info if provided - use user_id as key
    if (participant1Info || participant2Info) {
      chatData.participantInfo = {};
      if (participant1Info) {
        // Use user_id from participant1Info, fallback to userId1
        const participant1UserId = toUserIdString(participant1Info.user_id || uid1);
        chatData.participantInfo[participant1UserId] = {
          name: participant1Info.name || participant1Info.first_name || "",
          first_name: participant1Info.first_name || "",
          suffix: participant1Info.suffix || null,
          special_name: participant1Info.special_name || null,
          color_name: participant1Info.color_name || "",
          hex_code: participant1Info.hex_code || "#000000",
          head_first_name: participant1Info.head_first_name || "",
          head_suffix: participant1Info.head_suffix || null,
          head_special_name: participant1Info.head_special_name || null,
        };
      }
      if (participant2Info) {
        // Use user_id from participant2Info, fallback to userId2
        const participant2UserId = toUserIdString(participant2Info.user_id || uid2);
        chatData.participantInfo[participant2UserId] = {
          name: participant2Info.name || participant2Info.first_name || "",
          first_name: participant2Info.first_name || "",
          suffix: participant2Info.suffix || null,
          special_name: participant2Info.special_name || null,
          color_name: participant2Info.color_name || "",
          hex_code: participant2Info.hex_code || "#000000",
          head_first_name: participant2Info.head_first_name || "",
          head_suffix: participant2Info.head_suffix || null,
          head_special_name: participant2Info.head_special_name || null,
        };
      }
    }

    // Use setDoc with custom ID instead of addDoc
    await setDoc(chatRef, chatData);

    return chatId;
  } catch (error) {
    console.error("Error creating individual chat:", error);
    throw error;
  }
};

/**
 * Mark a chat as Admin Support (used when user contacts admin via Contact Admin)
 * @param {string} chatId - Chat document ID
 */
export const setChatAsAdminSupport = async (chatId) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    await updateDoc(chatRef, { isAdminSupport: true });
  } catch (error) {
    console.error("Error marking chat as admin support:", error);
    throw error;
  }
};

/**
 * Create a group chat room
 * @param {string} createdBy - User ID who created the group
 * @param {string} groupName - Name of the group
 * @param {Array<string>} participantIds - Array of user IDs (excluding creator)
 * @param {string} familyTreeId - Family tree ID
 * @param {Object} participantInfoMap - Map of user_id to participant info (including creator)
 * @returns {Promise<string>} - Chat room ID
 */
export const createGroupChat = async (createdBy, groupName, participantIds, familyTreeId, participantInfoMap = {}) => {
  try {
    // Ensure creator is in participants
    const participants = [...new Set([createdBy, ...participantIds])].sort();

    if (participants.length < 2) {
      throw new Error("Group chat must have at least 2 participants");
    }

    // Generate deterministic chat ID: family_tree_id_group_sortedParticipantIds
    // For group chats, we'll use sorted IDs joined by underscore, but limit length
    // Firestore document IDs have a max length, so we'll use a shorter format
    const sortedIds = participants.sort().join('_');
    // Create a hash-like string from sorted IDs (first 8 chars of each ID)
    const shortIds = participants.sort().map(id => id.substring(0, 8)).join('_');
    const chatId = `${familyTreeId}_group_${shortIds}`;
    
    // Firestore document ID max length is 1500 chars, but we'll keep it reasonable
    // If still too long, truncate
    const maxLength = 1500;
    const finalChatId = chatId.length > maxLength ? chatId.substring(0, maxLength) : chatId;

    const chatRef = doc(db, "chats", finalChatId);
    const chatSnap = await getDoc(chatRef);

    if (chatSnap.exists()) {
      // Update existing group chat with participant info if provided
      const updateData = {};
      
      // Update participant info for all participants
      participants.forEach((userId) => {
        const info = participantInfoMap[userId];
        if (info) {
          updateData[`participantInfo.${info.user_id || userId}`] = {
            name: info.name || info.first_name || "",
            first_name: info.first_name || "",
            suffix: info.suffix || null,
            special_name: info.special_name || null,
            color_name: info.color_name || "",
            hex_code: info.hex_code || "#000000",
            head_first_name: info.head_first_name || "",
            head_suffix: info.head_suffix || null,
            head_special_name: info.head_special_name || null,
          };
        }
      });
      
      if (Object.keys(updateData).length > 0) {
        updateData.updatedAt = serverTimestamp();
        await updateDoc(chatRef, updateData);
      }
      
      return finalChatId;
    }

    // Create new group chat
    const chatData = {
      type: "group",
      family_tree_id: familyTreeId,
      groupName: groupName.trim(),
      participants: participants,
      createdBy: createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Add participant info if provided
    if (Object.keys(participantInfoMap).length > 0) {
      chatData.participantInfo = {};
      participants.forEach((userId) => {
        const info = participantInfoMap[userId];
        if (info) {
          // Use user_id from info, fallback to userId
          const participantUserId = info.user_id || userId;
          chatData.participantInfo[participantUserId] = {
            name: info.name || info.first_name || "",
            first_name: info.first_name || "",
            suffix: info.suffix || null,
            special_name: info.special_name || null,
            color_name: info.color_name || "",
            hex_code: info.hex_code || "#000000",
            head_first_name: info.head_first_name || "",
            head_suffix: info.head_suffix || null,
            head_special_name: info.head_special_name || null,
          };
        }
      });
    }

    // Use setDoc with custom ID
    await setDoc(chatRef, chatData);

    return finalChatId;
  } catch (error) {
    console.error("Error creating group chat:", error);
    throw error;
  }
};

/**
 * Send a message to a chat room
 * @param {string} chatId - Chat room ID
 * @param {string} senderId - User ID of sender
 * @param {string} text - Message text
 * @param {string} senderName - Optional sender name for display
 * @param {string} messageType - Type of message ('text', 'image', 'file')
 * @param {Object} attachmentMeta - Optional attachment metadata { fileUrl, fileName, mimeType, fileExtension }
 * @returns {Promise<string>} - Message ID
 */
export const sendMessage = async (
  chatId,
  senderId,
  text,
  senderName = null,
  messageType = "text",
  attachmentMeta = null
) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    const messagesRef = collection(chatRef, "messages");

    const fileUrl = attachmentMeta?.fileUrl || null;
    const fileName = attachmentMeta?.fileName || null;
    const mimeType = attachmentMeta?.mimeType || null;
    const fileExtension = attachmentMeta?.fileExtension || null;

    const isAttachment = messageType === "image" || messageType === "file";
    const displayText = isAttachment
      ? messageType === "image"
        ? "Photo"
        : fileName || "File"
      : text.trim();

    const messagePayload = {
      text: displayText,
      senderId: senderId,
      senderName: senderName,
      timestamp: serverTimestamp(),
      readBy: [senderId],
      type: messageType,
    };

    if (fileUrl) messagePayload.fileUrl = fileUrl;
    if (fileName) messagePayload.fileName = fileName;
    if (mimeType) messagePayload.mimeType = mimeType;
    if (fileExtension) messagePayload.fileExtension = fileExtension;

    const lastMessageText = isAttachment
      ? messageType === "image"
        ? "Photo"
        : fileName || "File"
      : text.trim();

    // Add message to subcollection
    const messageRef = await addDoc(messagesRef, messagePayload);

    // Update chat's last message and timestamp
    await updateDoc(chatRef, {
      lastMessage: {
        text: lastMessageText,
        senderId: senderId,
        senderName: senderName,
        timestamp: serverTimestamp(),
        type: messageType,
      },
      lastMessageAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return messageRef.id;
  } catch (error) {
    console.error("Error sending message:", error);
    throw error;
  }
};

/**
 * Get all chats for a user (real-time listener)
 * @param {string} userId - User ID
 * @param {string} familyTreeId - Family tree ID
 * @param {Function} callback - Callback function that receives chats array
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToUserChats = (userId, familyTreeId, callback) => {
  try {
    const chatsRef = collection(db, "chats");
    const q = query(
      chatsRef,
      where("family_tree_id", "==", familyTreeId),
      where("participants", "array-contains", userId)
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const chats = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        chats.sort((a, b) => {
          const getTimestamp = (chat) => {
            if (chat.lastMessageAt?.toMillis) {
              return chat.lastMessageAt.toMillis();
            }
            if (chat.updatedAt?.toMillis) {
              return chat.updatedAt.toMillis();
            }
            if (chat.createdAt?.toMillis) {
              return chat.createdAt.toMillis();
            }
            if (chat.lastMessageAt) {
              return new Date(chat.lastMessageAt).getTime();
            }
            if (chat.updatedAt) {
              return new Date(chat.updatedAt).getTime();
            }
            if (chat.createdAt) {
              return new Date(chat.createdAt).getTime();
            }
            return 0;
          };

          return getTimestamp(b) - getTimestamp(a);
        });
        callback(chats);
      },
      (error) => {
        console.error("Error subscribing to chats:", error);
        if (error.code === "failed-precondition") {
          console.warn("Firestore index required. Please create the index in Firebase Console.");
        }
        callback([]);
      }
    );
  } catch (error) {
    console.error("Error setting up chat subscription:", error);
    return () => {};
  }
};

/**
 * Get messages for a chat room (real-time listener)
 * @param {string} chatId - Chat room ID
 * @param {Function} callback - Callback function that receives messages array
 * @param {number} limitCount - Maximum number of messages to fetch (default: 50)
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToMessages = (chatId, callback, limitCount = 50) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    const messagesRef = collection(chatRef, "messages");
    const q = query(
      messagesRef,
      orderBy("timestamp", "asc"),
      limit(limitCount)
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const messages = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        callback(messages);
      },
      (error) => {
        console.error("Error subscribing to messages:", error);
        callback([]);
      }
    );
  } catch (error) {
    console.error("Error setting up message subscription:", error);
    return () => {};
  }
};

/**
 * Get paginated messages for a chat room (for loading older messages)
 * @param {string} chatId - Chat room ID
 * @param {Timestamp} startAfter - Timestamp to start after (for pagination)
 * @param {number} limitCount - Number of messages to fetch
 * @returns {Promise<Array>} - Array of messages
 */
export const getMessagesPaginated = async (
  chatId,
  startAfter = null,
  limitCount = 20
) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    const messagesRef = collection(chatRef, "messages");

    let q = query(
      messagesRef,
      orderBy("timestamp", "desc"),
      limit(limitCount)
    );

    if (startAfter) {
      q = query(
        messagesRef,
        orderBy("timestamp", "desc"),
        startAfter(startAfter),
        limit(limitCount)
      );
    }

    const snapshot = await getDocs(q);
    const messages = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Reverse to get chronological order
    return messages.reverse();
  } catch (error) {
    console.error("Error getting paginated messages:", error);
    throw error;
  }
};

/**
 * Mark messages as read
 * @param {string} chatId - Chat room ID
 * @param {string} userId - User ID marking messages as read
 * @param {Array<string>} messageIds - Array of message IDs to mark as read (optional, if not provided, marks all unread)
 */
export const markMessagesAsRead = async (chatId, userId, messageIds = null) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    const messagesRef = collection(chatRef, "messages");

    if (messageIds && messageIds.length > 0) {
      // Mark specific messages as read
      const updatePromises = messageIds.map((messageId) => {
        const messageRef = doc(messagesRef, messageId);
        return updateDoc(messageRef, {
          readBy: arrayUnion(userId),
        });
      });
      await Promise.all(updatePromises);
    } else {
      // Mark all unread messages in the chat as read
      const q = query(messagesRef, orderBy("timestamp", "desc"));
      const snapshot = await getDocs(q);

      const updatePromises = snapshot.docs
        .filter((doc) => !doc.data().readBy?.includes(userId))
        .map((doc) => {
          return updateDoc(doc.ref, {
            readBy: arrayUnion(userId),
          });
        });

      await Promise.all(updatePromises);
    }
  } catch (error) {
    console.error("Error marking messages as read:", error);
    throw error;
  }
};

/**
 * Get a single chat room by ID
 * @param {string} chatId - Chat room ID
 * @returns {Promise<Object>} - Chat room data
 */
export const getChatById = async (chatId) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    const chatSnap = await getDoc(chatRef);

    if (chatSnap.exists()) {
      return {
        id: chatSnap.id,
        ...chatSnap.data(),
      };
    } else {
      throw new Error("Chat not found");
    }
  } catch (error) {
    console.error("Error getting chat:", error);
    throw error;
  }
};

/**
 * Update group chat details (name, participants)
 * @param {string} chatId - Chat room ID
 * @param {Object} updates - Object with fields to update (groupName, participants)
 * @returns {Promise<void>}
 */
export const updateGroupChat = async (chatId, updates) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(chatRef, updateData);
  } catch (error) {
    console.error("Error updating group chat:", error);
    throw error;
  }
};

/**
 * Add participants to a group chat
 * @param {string} chatId - Chat room ID
 * @param {Array<string>} userIds - Array of user IDs to add
 * @returns {Promise<void>}
 */
export const addParticipantsToGroup = async (chatId, userIds) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    const chatSnap = await getDoc(chatRef);

    if (!chatSnap.exists()) {
      throw new Error("Chat not found");
    }

    const chatData = chatSnap.data();
    if (chatData.type !== "group") {
      throw new Error("Can only add participants to group chats");
    }

    const currentParticipants = chatData.participants || [];
    const newParticipants = [...new Set([...currentParticipants, ...userIds])];

    await updateDoc(chatRef, {
      participants: newParticipants,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error adding participants:", error);
    throw error;
  }
};

/**
 * Remove participants from a group chat
 * @param {string} chatId - Chat room ID
 * @param {Array<string>} userIds - Array of user IDs to remove
 * @returns {Promise<void>}
 */
export const removeParticipantsFromGroup = async (chatId, userIds) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    const chatSnap = await getDoc(chatRef);

    if (!chatSnap.exists()) {
      throw new Error("Chat not found");
    }

    const chatData = chatSnap.data();
    if (chatData.type !== "group") {
      throw new Error("Can only remove participants from group chats");
    }

    const currentParticipants = chatData.participants || [];
    const newParticipants = currentParticipants.filter(
      (id) => !userIds.includes(id)
    );

    if (newParticipants.length < 2) {
      throw new Error("Group chat must have at least 2 participants");
    }

    await updateDoc(chatRef, {
      participants: newParticipants,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error removing participants:", error);
    throw error;
  }
};

/**
 * Delete a chat room (and all its messages)
 * @param {string} chatId - Chat room ID
 * @returns {Promise<void>}
 */
export const deleteChat = async (chatId) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    const messagesRef = collection(chatRef, "messages");

    // Delete all messages first
    const messagesSnapshot = await getDocs(messagesRef);
    const deleteMessagePromises = messagesSnapshot.docs.map((doc) =>
      deleteDoc(doc.ref)
    );
    await Promise.all(deleteMessagePromises);

    // Delete the chat document
    await deleteDoc(chatRef);
  } catch (error) {
    console.error("Error deleting chat:", error);
    throw error;
  }
};

/**
 * Update (edit) a message. Only updates text; sets edited and editedAt.
 * @param {string} chatId - Chat room ID
 * @param {string} messageId - Message ID
 * @param {string} newText - New message text
 * @returns {Promise<void>}
 */
export const updateMessage = async (chatId, messageId, newText) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    const messageRef = doc(collection(chatRef, "messages"), messageId);
    await updateDoc(messageRef, {
      text: newText.trim(),
      edited: true,
      editedAt: serverTimestamp(),
    });

    // If this message is the last one, update chat's lastMessage
    const messagesRef = collection(chatRef, "messages");
    const q = query(messagesRef, orderBy("timestamp", "desc"), limit(1));
    const snapshot = await getDocs(q);
    if (!snapshot.empty && snapshot.docs[0].id === messageId) {
      const lastMessageData = snapshot.docs[0].data();
      await updateDoc(chatRef, {
        lastMessage: {
          ...lastMessageData,
          text: newText.trim(),
          edited: true,
        },
        updatedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error("Error updating message:", error);
    throw error;
  }
};

/**
 * Delete a single message (hard delete - removes document)
 * @param {string} chatId - Chat room ID
 * @param {string} messageId - Message ID
 * @returns {Promise<void>}
 */
export const deleteMessage = async (chatId, messageId) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    const messageRef = doc(collection(chatRef, "messages"), messageId);
    await deleteDoc(messageRef);

    // Update last message if this was the last message
    const messagesRef = collection(chatRef, "messages");
    const q = query(messagesRef, orderBy("timestamp", "desc"), limit(1));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      // No messages left, clear last message
      await updateDoc(chatRef, {
        lastMessage: null,
        lastMessageAt: null,
        updatedAt: serverTimestamp(),
      });
    } else {
      // Update with the new last message
      const lastMessage = snapshot.docs[0].data();
      await updateDoc(chatRef, {
        lastMessage: {
          text: lastMessage.text,
          senderId: lastMessage.senderId,
          senderName: lastMessage.senderName || null, // Include senderName if available
          timestamp: lastMessage.timestamp,
        },
        lastMessageAt: lastMessage.timestamp,
        updatedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error("Error deleting message:", error);
    throw error;
  }
};

/**
 * Soft-delete a message (mark as deleted; others see "This message was deleted.")
 * @param {string} chatId - Chat room ID
 * @param {string} messageId - Message ID
 * @param {string} deletedByUserId - User ID who deleted the message
 * @returns {Promise<void>}
 */
export const softDeleteMessage = async (chatId, messageId, deletedByUserId) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    const messageRef = doc(collection(chatRef, "messages"), messageId);
    await updateDoc(messageRef, {
      deleted: true,
      deletedAt: serverTimestamp(),
      deletedBy: deletedByUserId,
    });

    // Update chat lastMessage if this was the last non-deleted message
    const messagesRef = collection(chatRef, "messages");
    const q = query(messagesRef, orderBy("timestamp", "desc"), limit(20));
    const snapshot = await getDocs(q);
    let lastNonDeleted = null;
    for (const d of snapshot.docs) {
      const data = d.data();
      if (!data.deleted) {
        lastNonDeleted = { id: d.id, ...data };
        break;
      }
    }
    await updateDoc(chatRef, {
      lastMessage: lastNonDeleted
        ? {
            text: lastNonDeleted.text,
            senderId: lastNonDeleted.senderId,
            senderName: lastNonDeleted.senderName || null,
            timestamp: lastNonDeleted.timestamp,
          }
        : null,
      lastMessageAt: lastNonDeleted?.timestamp || null,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error soft-deleting message:", error);
    throw error;
  }
};

/**
 * Add a system message (e.g. "X has left the group chat")
 * @param {string} chatId - Chat room ID
 * @param {string} text - System message text
 * @returns {Promise<string>} - Message ID
 */
export const addSystemMessage = async (chatId, text) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    const messagesRef = collection(chatRef, "messages");
    const messageRef = await addDoc(messagesRef, {
      type: "system",
      text: text.trim(),
      timestamp: serverTimestamp(),
    });
    await updateDoc(chatRef, {
      lastMessage: { text: text.trim(), senderId: null, timestamp: serverTimestamp() },
      lastMessageAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return messageRef.id;
  } catch (error) {
    console.error("Error adding system message:", error);
    throw error;
  }
};

/**
 * Merge legacy blockedByUserId into individualChatBlocks shape for reads.
 * individualChatBlocks: { [userIdWhoCannotSend]: userIdWhoBlockedThem }
 */
export const getIndividualChatBlocksMap = (chat) => {
  const blocks = { ...(chat?.individualChatBlocks || {}) };
  const legacy = chat?.blockedByUserId;
  const participants = chat?.participants || [];
  if (legacy && blocks[legacy] == null && participants.length === 2) {
    const blocker = participants.find((id) => String(id) !== String(legacy));
    if (blocker) blocks[legacy] = blocker;
  }
  return blocks;
};

/**
 * Block a user in an individual chat (either participant can block the other).
 * @param {string} chatId
 * @param {string} blockerUserId - Current user (who is blocking)
 * @param {string} blockedUserId - The other participant (cannot send until unblocked)
 */
export const blockUserInChat = async (chatId, blockerUserId, blockedUserId) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    const snap = await getDoc(chatRef);
    if (!snap.exists()) throw new Error("Chat not found");
    const data = snap.data();
    const p = data.participants || [];
    const isIndividual =
      data.type === "individual" ||
      (data.type !== "group" && p.length === 2);
    if (!isIndividual) throw new Error("Can only block in a one-to-one chat");
    const inParticipants = (id) => p.some((x) => String(x) === String(id));
    const canonical = (id) => p.find((x) => String(x) === String(id));
    if (!blockerUserId || !blockedUserId || String(blockerUserId) === String(blockedUserId)) {
      throw new Error("Invalid block");
    }
    if (!inParticipants(blockerUserId) || !inParticipants(blockedUserId)) {
      throw new Error("Invalid participants");
    }
    const blockerCanon = canonical(blockerUserId);
    const blockedCanon = canonical(blockedUserId);

    const updates = {
      [`individualChatBlocks.${blockedCanon}`]: blockerCanon,
      updatedAt: serverTimestamp(),
    };
    if (data.blockedByUserId != null) {
      const victim = data.blockedByUserId;
      const legacyBlocker = p.find((id) => String(id) !== String(victim));
      if (legacyBlocker) {
        const victimCanon = canonical(victim) || victim;
        const lb = canonical(legacyBlocker) || legacyBlocker;
        updates[`individualChatBlocks.${victimCanon}`] = lb;
      }
      updates.blockedByUserId = deleteField();
    }
    await updateDoc(chatRef, updates);
  } catch (error) {
    console.error("Error blocking user:", error);
    throw error;
  }
};

const findVictimKeyInBlocks = (blocks, blockedUserId) => {
  if (!blocks || blockedUserId == null) return null;
  const k = Object.keys(blocks).find((key) => String(key) === String(blockedUserId));
  return k ?? null;
};

/**
 * Remove a block placed by blockerUserId on blockedUserId (only that blocker can unblock).
 */
export const unblockUserInChat = async (chatId, blockerUserId, blockedUserId) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    const snap = await getDoc(chatRef);
    if (!snap.exists()) throw new Error("Chat not found");
    const data = snap.data();
    const p = data.participants || [];
    const blocks = getIndividualChatBlocksMap({ ...data, participants: p });
    const victimKey = findVictimKeyInBlocks(blocks, blockedUserId);
    const blockerForVictim = victimKey != null ? blocks[victimKey] : null;
    const matches =
      victimKey != null && String(blockerForVictim) === String(blockerUserId);

    if (matches) {
      if (data.individualChatBlocks && data.individualChatBlocks[victimKey] != null) {
        await updateDoc(chatRef, {
          [`individualChatBlocks.${victimKey}`]: deleteField(),
          updatedAt: serverTimestamp(),
        });
      } else {
        await updateDoc(chatRef, {
          blockedByUserId: deleteField(),
          updatedAt: serverTimestamp(),
        });
      }
      return;
    }
    throw new Error("You can only remove a block you created");
  } catch (error) {
    console.error("Error unblocking user:", error);
    throw error;
  }
};

/**
 * Block a member in a group chat (only group creator can block)
 * @param {string} chatId - Chat room ID
 * @param {string} memberUserId - User ID to block
 * @returns {Promise<void>}
 */
export const blockMemberInGroupChat = async (chatId, memberUserId) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    await updateDoc(chatRef, {
      blockedMembers: arrayUnion(memberUserId),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error blocking member in group:", error);
    throw error;
  }
};

/**
 * Unblock a member in a group chat (only group creator should call this)
 * @param {string} chatId - Chat room ID
 * @param {string} memberUserId - User ID to unblock
 * @returns {Promise<void>}
 */
export const unblockMemberInGroupChat = async (chatId, memberUserId) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    await updateDoc(chatRef, {
      blockedMembers: arrayRemove(memberUserId),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error unblocking member in group:", error);
    throw error;
  }
};

/**
 * Leave a group chat (removes user from participants and adds "X has left the group chat")
 * @param {string} chatId - Chat room ID
 * @param {string} userId - User ID who is leaving
 * @param {string} userName - Display name for the system message
 * @returns {Promise<void>}
 */
export const leaveGroupChat = async (chatId, userId, userName) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    const chatSnap = await getDoc(chatRef);
    if (!chatSnap.exists()) throw new Error("Chat not found");
    const chatData = chatSnap.data();
    if (chatData.type !== "group") throw new Error("Can only leave group chats");

    const displayName = userName || "A member";
    await addSystemMessage(chatId, `${displayName} has left the group chat`);

    const currentParticipants = chatData.participants || [];
    const newParticipants = currentParticipants.filter((id) => id !== userId);
    await updateDoc(chatRef, {
      participants: newParticipants,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error leaving group chat:", error);
    throw error;
  }
};

/**
 * Get unread message count for a user in a specific chat
 * @param {string} chatId - Chat room ID
 * @param {string} userId - User ID
 * @returns {Promise<number>} - Count of unread messages
 */
export const getUnreadMessageCount = async (chatId, userId) => {
  try {
    const chatRef = doc(db, "chats", chatId);
    const messagesRef = collection(chatRef, "messages");

    const q = query(messagesRef, orderBy("timestamp", "desc"));
    const snapshot = await getDocs(q);

    let unreadCount = 0;
    snapshot.docs.forEach((doc) => {
      const messageData = doc.data();
      if (
        messageData.senderId !== userId &&
        !messageData.readBy?.includes(userId)
      ) {
        unreadCount++;
      }
    });

    return unreadCount;
  } catch (error) {
    console.error("Error getting unread count:", error);
    throw error;
  }
};

/**
 * Get total unread message count across all chats for a user
 * @param {string} userId - User ID
 * @param {Array<string>} chatIds - Array of chat IDs
 * @returns {Promise<number>} - Total count of unread messages
 */
export const getTotalUnreadCount = async (userId, chatIds) => {
  if (!userId || !chatIds?.length) return 0;
  try {
    const counts = await Promise.all(
      chatIds.map((chatId) => getUnreadMessageCount(chatId, userId))
    );
    return counts.reduce((sum, n) => sum + n, 0);
  } catch (error) {
    console.error("Error getting total unread count:", error);
    return 0;
  }
};

/**
 * Get the other participant's ID in an individual chat
 * @param {Object} chat - Chat object
 * @param {string} currentUserId - Current user ID
 * @returns {string|null} - Other participant's ID or null
 */
/**
 * Update participant info in a chat document if missing
 * @param {string} chatId - Chat room ID
 * @param {string} userId - User ID whose info needs to be updated
 * @param {Object} participantInfo - Participant's member info (name, color, head of line, etc.)
 * @returns {Promise<void>}
 */
export const updateParticipantInfo = async (chatId, userId, participantInfo) => {
  try {
    if (!participantInfo) return;

    const chatRef = doc(db, "chats", chatId);
    const chatSnap = await getDoc(chatRef);

    if (!chatSnap.exists()) {
      console.error("Chat not found:", chatId);
      return;
    }

    const chatData = chatSnap.data();
    
    // Check if participant info already exists for this user
    if (chatData.participantInfo && chatData.participantInfo[userId]) {
      // Info already exists, no need to update
      return;
    }

    // Update participant info
    const updateData = {
      [`participantInfo.${userId}`]: {
        name: participantInfo.name || participantInfo.first_name || "",
        first_name: participantInfo.first_name || "",
        suffix: participantInfo.suffix || null,
        special_name: participantInfo.special_name || null,
        color_name: participantInfo.color_name || "",
        hex_code: participantInfo.hex_code || "#000000",
        head_first_name: participantInfo.head_first_name || "",
        head_suffix: participantInfo.head_suffix || null,
        head_special_name: participantInfo.head_special_name || null,
      },
      updatedAt: serverTimestamp(),
    };

    await updateDoc(chatRef, updateData);
    console.log(`Updated participant info for ${userId} in chat ${chatId}`);
  } catch (error) {
    console.error("Error updating participant info:", error);
    // Don't throw - this is a non-critical update
  }
};

export const getOtherParticipantId = (chat, currentUserId) => {
  if (!chat?.participants || currentUserId == null) return null;
  const isOneToOne =
    chat.type === "individual" ||
    (chat.type !== "group" && chat.participants.length === 2);
  if (isOneToOne) {
    return chat.participants.find((id) => String(id) !== String(currentUserId)) ?? null;
  }
  return null;
};

/**
 * The logged-in user id may differ from the id stored in chat.participants (e.g. admin
 * uses family-tree user id in userDetail but Admin Support chat lists admin_details.id).
 */
export const resolveMyParticipantIdInChat = (chat, currentUserId) => {
  const p = chat?.participants;
  if (!p?.length || currentUserId == null) return currentUserId;
  const canon = (id) => p.find((x) => String(x) === String(id));
  if (canon(currentUserId)) return canon(currentUserId);
  try {
    if (typeof localStorage !== "undefined") {
      const adminId = getAdminUserIdFromDetails(getAdminDetailsFromStorage());
      if (adminId != null && canon(adminId)) return canon(adminId);
    }
  } catch {
    /* ignore */
  }
  return currentUserId;
};

/**
 * Format timestamp for display
 * @param {Timestamp} timestamp - Firestore timestamp
 * @returns {string} - Formatted date string
 */
export const formatMessageTime = (timestamp) => {
  if (!timestamp) return "";

  const date = timestamp.toDate();
  const now = new Date();
  const diffInMs = now - date;
  const diffInMins = Math.floor(diffInMs / 60000);
  const diffInHours = Math.floor(diffInMs / 3600000);
  const diffInDays = Math.floor(diffInMs / 86400000);

  if (diffInMins < 1) return "Just now";
  if (diffInMins < 60) return `${diffInMins}m Ago`;
  if (diffInHours < 24) return `${diffInHours}h Ago`;
  if (diffInDays < 7) return `${diffInDays}d Ago`;

  // Format as date
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
};
